import { useState, useEffect } from 'react';
import { AppLayout } from '../components/Layout/AppLayout';
import { useGroups, Group } from '../hooks/useGroups';
import { useMembers } from '../hooks/useMembers';
import { useCredentials } from '../hooks/useCredentials';
import { useAuth } from '../hooks/useAuth';
import { Shield, Plus, X, Search, Users, KeyRound } from 'lucide-react';
import { PageHeader } from '../components/common/PageHeader';
import { Modal } from '../components/common/Modal';
import { useFeedback } from '../components/common/Feedback';
import { formatarNome } from '../utils/nomes';

// M11: a busca só aparece quando a lista passa disso (com poucos grupos ela só ocupa espaço).
const GRUPOS_PARA_MOSTRAR_BUSCA = 6;

export function Groups() {
  const { groups, isLoading, fetchGroups, createGroup, associateCredentials, associateMembers } = useGroups();
  const { members, fetchMembers } = useMembers();
  const { credentials, fetchCredentials } = useCredentials();
  const { membership } = useAuth();

  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDesc, setNewGroupDesc] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const { avisar } = useFeedback();
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);
  const [selectedCredentialIds, setSelectedCredentialIds] = useState<string[]>([]);

  // Search states
  const [groupSearch, setGroupSearch] = useState('');
  const [memberSearch, setMemberSearch] = useState('');
  const [credentialSearch, setCredentialSearch] = useState('');

  useEffect(() => {
    if (membership?.role === 'admin' || membership?.role === 'owner') {
      fetchGroups();
      fetchMembers();
      fetchCredentials();
    }
  }, [fetchGroups, fetchMembers, fetchCredentials, membership]);

  const handleOpenModal = (group: Group) => {
    setSelectedGroup(group);
    setSelectedMemberIds(group.member_ids ?? []);
    setSelectedCredentialIds(group.credential_ids ?? []);
    setMemberSearch('');
    setCredentialSearch('');
  };

  const handleCloseModal = () => {
    setSelectedGroup(null);
    setSelectedMemberIds([]);
    setSelectedCredentialIds([]);
    setMemberSearch('');
    setCredentialSearch('');
  };

  const toggleMember = (id: string) => {
    setSelectedMemberIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleCredential = (id: string) => {
    setSelectedCredentialIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  // Filtered lists for the modal
  const filteredMembers = members.filter(member => {
    const term = memberSearch.toLowerCase().trim();
    if (!term) return true;
    const nameMatch = (member.name || '').toLowerCase().includes(term);
    const emailMatch = (member.email || '').toLowerCase().includes(term);
    return nameMatch || emailMatch;
  });

  const filteredCredentials = credentials.filter(cred => {
    const term = credentialSearch.toLowerCase().trim();
    if (!term) return true;
    const labelMatch = (cred.label || '').toLowerCase().includes(term);
    const typeMatch = (cred.system_type || '').toLowerCase().includes(term);
    return labelMatch || typeMatch;
  });

  // Filtered groups for the main list
  const filteredGroups = groups.filter(group => {
    const term = groupSearch.toLowerCase().trim();
    if (!term) return true;
    const nameMatch = (group.name || '').toLowerCase().includes(term);
    const descMatch = (group.description || '').toLowerCase().includes(term);
    return nameMatch || descMatch;
  });

  const allFilteredMembersSelected =
    filteredMembers.length > 0 && filteredMembers.every(m => selectedMemberIds.includes(m.id));
  const allFilteredCredentialsSelected =
    filteredCredentials.length > 0 && filteredCredentials.every(c => selectedCredentialIds.includes(c.id));

  const toggleAllMembers = () => {
    if (allFilteredMembersSelected) {
      const filteredIds = new Set(filteredMembers.map(m => m.id));
      setSelectedMemberIds(prev => prev.filter(id => !filteredIds.has(id)));
    } else {
      const filteredIds = filteredMembers.map(m => m.id);
      setSelectedMemberIds(prev => Array.from(new Set([...prev, ...filteredIds])));
    }
  };

  const toggleAllCredentials = () => {
    if (allFilteredCredentialsSelected) {
      const filteredIds = new Set(filteredCredentials.map(c => c.id));
      setSelectedCredentialIds(prev => prev.filter(id => !filteredIds.has(id)));
    } else {
      const filteredIds = filteredCredentials.map(c => c.id);
      setSelectedCredentialIds(prev => Array.from(new Set([...prev, ...filteredIds])));
    }
  };

  const handleCreateGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newGroupName) return;
    setIsCreating(true);
    try {
      await createGroup({ name: newGroupName, description: newGroupDesc });
      avisar(`Grupo "${newGroupName}" criado.`, 'sucesso');
      setNewGroupName('');
      setNewGroupDesc('');
      setIsCreateOpen(false);
    } catch (err: any) {
      avisar(err?.message || 'Erro ao criar o grupo.', 'erro');
    } finally {
      setIsCreating(false);
    }
  };

  const closeCreate = () => {
    setIsCreateOpen(false);
    setNewGroupName('');
    setNewGroupDesc('');
  };

  const handleSaveAssociations = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedGroup) return;
    try {
      await associateMembers(selectedGroup.id, selectedMemberIds);
      await associateCredentials(selectedGroup.id, selectedCredentialIds);
      avisar(`Acessos do grupo "${selectedGroup.name}" salvos.`, 'sucesso');
      handleCloseModal();
    } catch (err: any) {
      avisar(err?.message || 'Erro ao salvar os acessos.', 'erro');
    }
  };

  const selectAllBtnStyle = (allSelected: boolean): React.CSSProperties => ({
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: 'var(--text-xs)',
    fontWeight: 'var(--font-medium)',
    color: allSelected ? 'var(--color-text-secondary)' : 'var(--color-primary-400)',
    padding: '2px 0',
    textDecoration: 'underline',
    textUnderlineOffset: '2px',
  });

  if (membership?.role !== 'admin' && membership?.role !== 'owner') {
    return (
      <AppLayout>
        <div style={{ color: 'var(--color-error)' }}>Acesso negado. Apenas administradores podem gerenciar grupos.</div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageHeader
        title="Grupos de acesso"
        description="Organize credenciais e atribua acesso aos membros em lote."
        actions={
          <button type="button" className="btn btn--primary btn--sm" onClick={() => setIsCreateOpen(true)}>
            <Plus size={16} /> Novo grupo
          </button>
        }
      />

      {/* M11: criar grupo é ação rara; o formulário abre num modal em vez de ocupar o topo da página. */}
      {isCreateOpen && (
        <Modal pedirParaFechar={closeCreate} protegerDigitacao rotuloId="novo-grupo-titulo">
          <div style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border-default)', borderRadius: 'var(--radius-xl)', width: '100%', maxWidth: '480px', padding: 'var(--space-6)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-5)' }}>
              <h2 id="novo-grupo-titulo" style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-semibold)' }}>Novo grupo</h2>
              <button type="button" onClick={closeCreate} aria-label="Fechar" title="Fechar" className="botao-icone botao-icone--sem-borda"><X size={20} /></button>
            </div>
            <form onSubmit={handleCreateGroup} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                <label htmlFor="novo-grupo-nome" style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>Nome do grupo</label>
                <input id="novo-grupo-nome" value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)} required placeholder="ex: Equipe Cível" style={{ background: 'var(--color-bg-root)', border: '1px solid var(--color-border-default)', padding: 'var(--space-3)', borderRadius: 'var(--radius-md)', color: 'var(--color-text-primary)' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                <label htmlFor="novo-grupo-descricao" style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>Descrição (opcional)</label>
                <input id="novo-grupo-descricao" value={newGroupDesc} onChange={(e) => setNewGroupDesc(e.target.value)} placeholder="Acesso aos tribunais da área cível" style={{ background: 'var(--color-bg-root)', border: '1px solid var(--color-border-default)', padding: 'var(--space-3)', borderRadius: 'var(--radius-md)', color: 'var(--color-text-primary)' }} />
              </div>
              <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end', flexWrap: 'wrap', marginTop: 'var(--space-2)' }}>
                <button type="button" className="btn btn--outline" onClick={closeCreate}>Cancelar</button>
                <button type="submit" className="btn btn--primary" disabled={!newGroupName || isCreating}>
                  <Plus size={16} /> {isCreating ? 'Criando...' : 'Criar grupo'}
                </button>
              </div>
            </form>
          </div>
        </Modal>
      )}

      {/* Grupos Header with Search */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
        <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-medium)', color: 'var(--color-text-primary)' }}>
          Grupos cadastrados {groups.length > 0 && `(${groups.length})`}
        </h3>
        {groups.length > GRUPOS_PARA_MOSTRAR_BUSCA && (
          <div style={{ position: 'relative', flex: '0 1 280px', minWidth: 0 }}>
            <input
              type="text"
              placeholder="Buscar grupo..."
              value={groupSearch}
              onChange={(e) => setGroupSearch(e.target.value)}
              style={{
                width: '100%',
                padding: 'var(--space-2) var(--space-4) var(--space-2) 34px',
                background: 'var(--color-bg-surface)',
                border: '1px solid var(--color-border-default)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--color-text-primary)',
                fontSize: 'var(--text-sm)',
                outline: 'none',
              }}
            />
            <Search
              size={16}
              style={{
                position: 'absolute',
                left: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--color-text-secondary)',
                pointerEvents: 'none',
              }}
            />
            {groupSearch && (
              <button
                type="button"
                onClick={() => setGroupSearch('')}
                aria-label="Limpar busca"
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--color-text-secondary)',
                  cursor: 'pointer',
                  padding: '2px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <X size={14} />
              </button>
            )}
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(320px, 100%), 1fr))', gap: 'var(--space-6)' }}>
        {isLoading ? (
          <div style={{ color: 'var(--color-text-secondary)' }}>Carregando grupos...</div>
        ) : filteredGroups.length === 0 ? (
          <div style={{ color: 'var(--color-text-secondary)', gridColumn: '1 / -1', padding: 'var(--space-6)', textAlign: 'center', background: 'var(--color-bg-surface)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border-default)' }}>
            {groupSearch ? 'Nenhum grupo encontrado com o termo buscado.' : 'Nenhum grupo cadastrado ainda.'}
          </div>
        ) : filteredGroups.map(group => (
          <div key={group.id} style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border-default)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-6)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-2)', minWidth: 0 }}>
              <Shield size={24} color="var(--color-primary-400)" style={{ flexShrink: 0 }} />
              <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-medium)', overflowWrap: 'anywhere' }}>{group.name}</h3>
            </div>
            {/* M11: sem descrição, nada de "Sem descrição" repetido */}
            {group.description && <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)', marginBottom: 'var(--space-2)' }}>{group.description}</p>}
            {/* M11: as contagens abrem o mesmo painel de acessos */}
            <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', fontSize: 'var(--text-sm)', margin: 'var(--space-2) 0 var(--space-4)', flex: 1, alignItems: 'flex-start' }}>
              <button type="button" className="link-contagem" onClick={() => handleOpenModal(group)} title="Ver e alterar os membros do grupo">
                <Users size={14} /> {group.member_ids?.length || 0} {(group.member_ids?.length || 0) === 1 ? 'membro' : 'membros'}
              </button>
              <button type="button" className="link-contagem" onClick={() => handleOpenModal(group)} title="Ver e alterar as credenciais do grupo">
                <KeyRound size={14} /> {group.credential_ids?.length || 0} {(group.credential_ids?.length || 0) === 1 ? 'credencial' : 'credenciais'}
              </button>
            </div>
            <button type="button" className="btn btn--outline btn--sm btn--full" onClick={() => handleOpenModal(group)}>
              Gerenciar acessos
            </button>
          </div>
        ))}
      </div>

      {selectedGroup && (
        <Modal pedirParaFechar={handleCloseModal} protegerDigitacao rotuloId="acessos-do-grupo-titulo">
          <div style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border-default)', borderRadius: 'var(--radius-xl)', width: '100%', maxWidth: '600px', padding: 'var(--space-8)', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-6)' }}>
              <h2 id="acessos-do-grupo-titulo" style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-semibold)', overflowWrap: 'anywhere' }}>Gerenciar acessos: {selectedGroup.name}</h2>
              <button type="button" onClick={handleCloseModal} aria-label="Fechar" title="Fechar" className="botao-icone botao-icone--sem-borda"><X size={24} /></button>
            </div>
            <form onSubmit={handleSaveAssociations}>
              {/* Seção Membros */}
              <div style={{ marginBottom: 'var(--space-6)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-2)' }}>
                  <h4 style={{ color: 'var(--color-text-primary)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)' }}>
                    Membros ({selectedMemberIds.length} selecionado{selectedMemberIds.length !== 1 ? 's' : ''})
                  </h4>
                  <button type="button" onClick={toggleAllMembers} style={selectAllBtnStyle(allFilteredMembersSelected)}>
                    {allFilteredMembersSelected ? (memberSearch ? 'Desmarcar visíveis' : 'Desmarcar todos') : (memberSearch ? 'Selecionar visíveis' : 'Selecionar todos')}
                  </button>
                </div>

                {/* Member Search Bar */}
                <div style={{ position: 'relative', marginBottom: 'var(--space-2)' }}>
                  <input
                    type="text"
                    placeholder="Buscar membro por nome ou email..."
                    value={memberSearch}
                    onChange={(e) => setMemberSearch(e.target.value)}
                    style={{
                      width: '100%',
                      padding: 'var(--space-2) var(--space-3) var(--space-2) 32px',
                      background: 'var(--color-bg-root)',
                      border: '1px solid var(--color-border-default)',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--color-text-primary)',
                      fontSize: 'var(--text-xs)',
                      outline: 'none',
                    }}
                  />
                  <Search
                    size={14}
                    style={{
                      position: 'absolute',
                      left: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: 'var(--color-text-secondary)',
                      pointerEvents: 'none',
                    }}
                  />
                  {memberSearch && (
                    <button
                      type="button"
                      onClick={() => setMemberSearch('')}
                      style={{
                        position: 'absolute',
                        right: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'transparent',
                        border: 'none',
                        color: 'var(--color-text-secondary)',
                        cursor: 'pointer',
                        padding: '2px',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>

                {/* Member List */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', maxHeight: '180px', overflowY: 'auto', background: 'var(--color-bg-root)', padding: 'var(--space-3)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border-default)' }}>
                  {filteredMembers.length === 0 ? (
                    <div style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-xs)', textAlign: 'center', padding: 'var(--space-3)' }}>
                      {memberSearch ? 'Nenhum membro encontrado.' : 'Nenhum membro cadastrado.'}
                    </div>
                  ) : (
                    filteredMembers.map(member => (
                      <label key={member.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={selectedMemberIds.includes(member.id)}
                          onChange={() => toggleMember(member.id)}
                        />
                        <span style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)' }}>{formatarNome(member.name) || member.email}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>

              {/* Seção Credenciais */}
              <div style={{ marginBottom: 'var(--space-6)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-2)' }}>
                  <h4 style={{ color: 'var(--color-text-primary)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)' }}>
                    Credenciais Permitidas ({selectedCredentialIds.length} selecionada{selectedCredentialIds.length !== 1 ? 's' : ''})
                  </h4>
                  <button type="button" onClick={toggleAllCredentials} style={selectAllBtnStyle(allFilteredCredentialsSelected)}>
                    {allFilteredCredentialsSelected ? (credentialSearch ? 'Desmarcar visíveis' : 'Desmarcar todos') : (credentialSearch ? 'Selecionar visíveis' : 'Selecionar todos')}
                  </button>
                </div>

                {/* Credential Search Bar */}
                <div style={{ position: 'relative', marginBottom: 'var(--space-2)' }}>
                  <input
                    type="text"
                    placeholder="Buscar credencial por nome ou tipo..."
                    value={credentialSearch}
                    onChange={(e) => setCredentialSearch(e.target.value)}
                    style={{
                      width: '100%',
                      padding: 'var(--space-2) var(--space-3) var(--space-2) 32px',
                      background: 'var(--color-bg-root)',
                      border: '1px solid var(--color-border-default)',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--color-text-primary)',
                      fontSize: 'var(--text-xs)',
                      outline: 'none',
                    }}
                  />
                  <Search
                    size={14}
                    style={{
                      position: 'absolute',
                      left: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: 'var(--color-text-secondary)',
                      pointerEvents: 'none',
                    }}
                  />
                  {credentialSearch && (
                    <button
                      type="button"
                      onClick={() => setCredentialSearch('')}
                      style={{
                        position: 'absolute',
                        right: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'transparent',
                        border: 'none',
                        color: 'var(--color-text-secondary)',
                        cursor: 'pointer',
                        padding: '2px',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>

                {/* Credential List */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', maxHeight: '180px', overflowY: 'auto', background: 'var(--color-bg-root)', padding: 'var(--space-3)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border-default)' }}>
                  {filteredCredentials.length === 0 ? (
                    <div style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-xs)', textAlign: 'center', padding: 'var(--space-3)' }}>
                      {credentialSearch ? 'Nenhuma credencial encontrada.' : 'Nenhuma credencial cadastrada.'}
                    </div>
                  ) : (
                    filteredCredentials.map(cred => (
                      <label key={cred.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={selectedCredentialIds.includes(cred.id)}
                          onChange={() => toggleCredential(cred.id)}
                        />
                        <span style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)' }}>
                          {cred.label} <span style={{ opacity: 0.7, fontSize: 'var(--text-xs)' }}>({cred.system_type})</span>
                        </span>
                      </label>
                    ))
                  )}
                </div>
              </div>

              {/* A2: rodapé fixo, Salvar sempre à vista */}
              <div style={{ display: 'flex', gap: 'var(--space-4)', position: 'sticky', bottom: 'calc(-1 * var(--space-8))', background: 'var(--color-bg-surface)', padding: 'var(--space-4) 0 var(--space-8)', marginBottom: 'calc(-1 * var(--space-8))', borderTop: '1px solid var(--color-border-default)' }}>
                <button type="button" className="btn btn--outline" onClick={handleCloseModal} style={{ flex: 1 }}>Cancelar</button>
                <button type="submit" className="btn btn--primary" style={{ flex: 1 }}>Salvar alterações</button>
              </div>
            </form>
          </div>
        </Modal>
      )}
    </AppLayout>
  );
}
